import { FC, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetLeadsDatabaseQuery,
  useGetMyLeadsQuery,
  useGetPurchasedLeadsQuery,
  usePurchaseAllLeadsMutation,
  usePurchaseLeadsMutation,
} from '@api/apiCompat';
import { ERROR, SUCCESS, WARNING } from '@constants/auth';
import { API_ERROR_MSG_PATH } from '@constants/common';
import NiceModal from '@ebay/nice-modal-react';
import { useCustomFiltersQueryParam } from '@hooks/common/useDynamicFilters/useCustomFiltersQueryParam';
import { usePageFilterUrlParams } from '@hooks/common/usePageFilterUrlParams';
import { transformQuery } from '@pages/CampaignDetails/utils';
import { getErrorMessage } from '@utils/getMessage';
import { useSnackbar } from 'notistack';
import qs from 'qs';

import { ScrapekingLeadsDbTable } from '@components/LeadsTable';
import { ExtendedPaginatedLeadsData } from '@components/LeadsTable/types';
import { PurchaseLeadsConfirmModalId } from '@components/PurchaseLeadsConfirmModal';

// GridRowSelectionModel is simply an array of row IDs
type GridRowSelectionModel = (string | number)[];

// Map lead_type filter value to backend campaignType ID
const LEAD_TYPE_TO_CAMPAIGN_TYPE: Record<string, number> = {
  local_search: 1,
  real_estate: 3,
  jobs: 4,
  social_media: 5,
};

// Map lead_type filter value to column display type
const LEAD_TYPE_TO_COLUMN_TYPE: Record<string, string> = {
  local_search: 'Local Search',
  real_estate: 'Real Estate',
  jobs: 'Job Sites',
  social_media: 'Social Media',
};

const ScrapekingDb: FC = () => {
  const { page, limit } = usePageFilterUrlParams();
  const { dynamicParam } = useCustomFiltersQueryParam();
  const snackbar = useSnackbar();
  const { t } = useTranslation();

  // Extract lead_type from filters to determine campaignType and column type
  // AutocompleteSearchFilter stores [{label, value}] arrays
  let leadTypeFilter = '';
  if (dynamicParam) {
    try {
      const parsed = JSON.parse(dynamicParam);
      const lt = parsed?.lead_type;
      if (Array.isArray(lt) && lt.length > 0) {
        leadTypeFilter = lt[0]?.value || '';
      } else if (typeof lt === 'string') {
        leadTypeFilter = lt;
      }
    } catch {
      // ignore parse errors
    }
  }

  const campaignTypeId = leadTypeFilter ? LEAD_TYPE_TO_CAMPAIGN_TYPE[leadTypeFilter] : undefined;
  const columnType = leadTypeFilter ? LEAD_TYPE_TO_COLUMN_TYPE[leadTypeFilter] : 'Local Search';

  const serializedFilters =
    dynamicParam && encodeURIComponent(qs.stringify(transformQuery(dynamicParam), { encode: false }));

  const {
    data: rawLeadsDbData,
    isLoading: isLoadingLeads,
    isFetching: isFetchingLeads,
    refetch: refetchDbLeads,
    error: leadsDbError,
  } = useGetLeadsDatabaseQuery(
    {
      page,
      limit,
      ...(campaignTypeId && { campaignType: campaignTypeId }),
      ...(dynamicParam && { filters: serializedFilters }),
    } as any,
    { refetchOnMountOrArgChange: true },
  );
  const leadsDbData = rawLeadsDbData as ExtendedPaginatedLeadsData | undefined;

  const {
    data: rawMyLeadsData,
    isLoading: isLoadingMyLeads,
    isFetching: isFetchingMyLeads,
    error: myLeadsError,
  } = useGetMyLeadsQuery({
    page: 0,
    limit: 25,
    ...(campaignTypeId && { campaignType: campaignTypeId }),
    ...(dynamicParam && { filters: serializedFilters }),
  } as any);
  const myLeadsData = rawMyLeadsData as { total?: number } | undefined;

  const { data: rawTotalPurchasedData, isLoading: isLoadingTotalPurchased } = useGetPurchasedLeadsQuery(
    {
      ...(campaignTypeId ? { campaignType: campaignTypeId } : {}),
    } as any,
    { refetchOnMountOrArgChange: true },
  );
  const totalPurchasedData = rawTotalPurchasedData as { totalPurchasedLeads?: number } | undefined;

  const [purchaseLeadsMutation, { isLoading: isLoadingPurchaseMutation, error: purchaseError }] =
    usePurchaseLeadsMutation();
  const [purchaseAllLeadsMutation, { isLoading: isLoadingPurchaseAllMutation, error: purchaseAllError }] =
    usePurchaseAllLeadsMutation();

  const handlePurchase = async (ids: GridRowSelectionModel) => {
    try {
      const result = await NiceModal.show(PurchaseLeadsConfirmModalId, {
        totalPrice: ids.length,
        totalOwned: myLeadsData && myLeadsData.total,
      });

      if (result) {
        await (purchaseLeadsMutation as any)({
          purchaseLeads: {
            id: [...(ids as number[])],
          },
        });
        snackbar.enqueueSnackbar(t('dashboard.tokens.tokensUsed', { totalPrice: ids.length }), {
          variant: WARNING,
        });

        if (purchaseError) {
          return snackbar.enqueueSnackbar(getErrorMessage(purchaseError, API_ERROR_MSG_PATH), { variant: ERROR });
        }

        snackbar.enqueueSnackbar(t('dashboard.db.leadsPurchased'), { variant: SUCCESS });
      }
    } catch (err) {
      snackbar.enqueueSnackbar(getErrorMessage(err, API_ERROR_MSG_PATH), { variant: ERROR });
    }
  };

  const handlePurchaseAll = async () => {
    try {
      const totalPrice =
        leadsDbData && totalPurchasedData
          ? (leadsDbData.total ?? 0) - (totalPurchasedData.totalPurchasedLeads ?? 0)
          : 0;
      const result = await NiceModal.show(PurchaseLeadsConfirmModalId, {
        totalPrice,
        totalOwned: myLeadsData && myLeadsData.total,
      });

      if (result) {
        await (purchaseAllLeadsMutation as any)({
          purchaseAllLeadsDto: {
            ...(campaignTypeId && { campaignType: campaignTypeId }),
            ...(dynamicParam && dynamicParam.length > 0 && { filters: serializedFilters }),
          },
        });
        snackbar.enqueueSnackbar(t('dashboard.tokens.tokensUsed', { totalPrice }), {
          variant: WARNING,
        });

        if (purchaseAllError) {
          return snackbar.enqueueSnackbar(getErrorMessage(purchaseAllError, API_ERROR_MSG_PATH), { variant: ERROR });
        }

        snackbar.enqueueSnackbar(t('dashboard.db.leadsPurchased'), { variant: SUCCESS });
      }
    } catch (err) {
      snackbar.enqueueSnackbar(getErrorMessage(err, API_ERROR_MSG_PATH), { variant: ERROR });
    }
  };

  const isLoading = [
    isLoadingLeads,
    isFetchingLeads,
    isLoadingTotalPurchased,
    isLoadingMyLeads,
    isFetchingMyLeads,
  ].some(Boolean);
  const isLoadingPurchase = [isLoadingPurchaseMutation, isLoadingPurchaseAllMutation].some(Boolean);

  // Handle errors in useEffect to avoid state updates during render
  useEffect(() => {
    const errorArray = [leadsDbError, myLeadsError].filter(Boolean);
    if (errorArray.length > 0) {
      snackbar.enqueueSnackbar(getErrorMessage(errorArray, API_ERROR_MSG_PATH), { variant: ERROR });
    }
  }, [leadsDbError, myLeadsError, snackbar]);

  return (
    <ScrapekingLeadsDbTable
      totalLeadsFound={leadsDbData?.total}
      leadsData={leadsDbData as ExtendedPaginatedLeadsData}
      isLoadingLeads={isLoading}
      refetch={refetchDbLeads}
      campaignType={columnType}
      isLoadingPurchaseMutation={isLoadingPurchase}
      handlePurchase={handlePurchase}
      handlePurchaseAll={handlePurchaseAll}
      totalPurchasedLeads={totalPurchasedData?.totalPurchasedLeads}
    />
  );
};

export default memo(ScrapekingDb);
